import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_ITEMS, INSTAGRAM_URL, waLink } from "@/lib/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-[70] transition-all duration-500 ${
          scrolled
            ? "border-b border-gold/10 bg-navy-950/85 py-3 backdrop-blur-md"
            : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
          <Logo />
          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-4 whitespace-nowrap xl:flex" aria-label="Navegação principal">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.href.replace("#", "")}`}
                className="whitespace-nowrap font-display text-[13px] font-semibold tracking-[0.08em] text-slate-300 transition-colors duration-200 hover:text-gold"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-instagram-link"
              aria-label="Instagram @realiza.predial"
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all duration-300 hover:border-gold hover:text-gold sm:flex"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-link"
              aria-label="WhatsApp da Realiza Engenharia"
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all duration-300 hover:border-gold hover:text-gold sm:flex"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
            <a
              href="#orcamento"
              data-testid="header-cta-btn"
              className="hidden rounded-full bg-gold px-5 py-2.5 font-display text-sm font-bold tracking-[0.12em] text-navy-950 transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] md:block"
            >
              SOLICITAR ORÇAMENTO
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-button"
              aria-label={open ? "Fechar menu" : "Abrir menu"}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-gold hover:text-gold xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col justify-center bg-navy-950/97 px-8 backdrop-blur-xl xl:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1" aria-label="Menu móvel">
              {NAV_ITEMS.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.href.replace("#", "")}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex items-baseline gap-4 border-b border-white/5 py-3"
                >
                  <span className="font-mono text-[11px] text-gold/60">0{i + 1}</span>
                  <span className="font-display text-3xl font-bold tracking-wide text-white transition-colors group-hover:text-gold">
                    {item.label}
                  </span>
                </motion.a>
              ))}
            </nav>
            <motion.a
              href="#orcamento"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-8 rounded-full bg-gold py-4 text-center font-display text-lg font-bold tracking-[0.14em] text-navy-950"
              data-testid="mobile-menu-cta"
            >
              SOLICITAR ORÇAMENTO
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
